// File: frontend/src/components/ui/motion.js
//
// §13.6 — motion communicates state; it does not decorate.
//
// THE MEASURED GAP THIS CLOSES
//   Before this phase, `prefers-reduced-motion` appeared in four files in the
//   whole repository — App.css, one dashboard panel and two marketing
//   components. None of the rebuilt Analytics, Packages or Transactions
//   surfaces honoured it, and between them they ran a dozen infinite skeleton
//   pulses, hover transforms and collapse animations. A user who has asked the
//   operating system to stop moving things was still being shown all of it.
//
// THE RULE
//   Every animation in the rebuilt surfaces goes through this module. Under
//   reduced motion the durations collapse to zero and the infinite pulses stop
//   at a STATIC tint — a skeleton still reads as a skeleton without breathing,
//   because its shape and its colour are what carry the meaning, not its pulse.
//
// WHY A HOOK AND NOT A CSS MEDIA QUERY
//   Both, in fact. `@media (prefers-reduced-motion: reduce)` inside an `sx`
//   block covers the purely declarative cases and costs nothing. But MUI's
//   transition components (Drawer, Collapse, Dialog, Menu) take their duration
//   as a JavaScript PROP, and no media query can reach it — those need the hook.
//   `useReducedMotion()` is a thin wrapper over `useMediaQuery` so there is one
//   spelling of the query and callers cannot typo it into always-false.

import { useMediaQuery } from "@mui/material";

/** The one query string. Typo it once and the feature silently never fires. */
export const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/**
 * Motion durations, in milliseconds. Four, not fifteen.
 *
 *   instant  state that must not appear to lag — a checkbox, a press
 *   fast     hover/focus affordances, colour and border changes
 *   base     a panel opening, a row expanding, a drawer sliding
 *   slow     reserved; nothing in the rebuilt surfaces currently needs it
 */
export const MOTION = {
  instant: 0,
  fast: 150,
  base: 220,
  slow: 320,
  easing: "cubic-bezier(0.4, 0, 0.2, 1)",
};

/**
 * True when the user has asked the OS to reduce motion.
 *
 * `noSsr` is deliberate: without it the first render always reports `false`,
 * which means a reduced-motion user sees one frame of the animation they asked
 * not to see, and MUI transition components get their duration prop AFTER they
 * have already started.
 */
export function useReducedMotion() {
  return useMediaQuery(REDUCED_MOTION_QUERY, { noSsr: true });
}

/**
 * A duration that is zero when motion is reduced. Pass this to MUI's
 * `transitionDuration` / `timeout` props.
 *
 *   <Drawer transitionDuration={duration(reduced, "base")} />
 *   <Collapse timeout={duration(reduced, "fast")} />
 */
export function duration(reduced, key = "base") {
  if (reduced) return 0;
  return typeof key === "number" ? key : (MOTION[key] ?? MOTION.base);
}

/**
 * A CSS `transition` value that becomes "none" under reduced motion.
 *
 *   sx={{ transition: transition(reduced, ["border-color", "box-shadow"]) }}
 */
export function transition(reduced, properties = ["all"], key = "fast") {
  if (reduced) return "none";
  const ms = duration(false, key);
  const list = Array.isArray(properties) ? properties : [properties];
  return list.map((p) => `${p} ${ms}ms ${MOTION.easing}`).join(", ");
}

/**
 * The skeleton shimmer, as an `sx` fragment — and the reason this module exists
 * in a phase about polish.
 *
 * The five rebuilt surfaces each declared their OWN `@keyframes` pulse with its
 * own name (`pkgPulse`, `pkgRowPulse`, `txRowPulse`, …) and its own timing, and
 * none of them stopped for reduced motion. This is one pulse, one name, and it
 * holds still when asked. Under reduced motion the element keeps its tint and
 * its shape and simply does not breathe — which is what a static skeleton is.
 *
 * The media query is inside the `sx` rather than read from a hook so that a
 * component rendering fifty skeleton cells does not need fifty hook calls; the
 * hook form is for the JS-prop cases.
 */
export function skeletonSx({ radius = 1, height = 18, width = "100%" } = {}) {
  return {
    height,
    width,
    borderRadius: radius,
    bgcolor: "action.hover",
    animation: "pesafiiSkeletonPulse 1.4s ease-in-out infinite",
    "@keyframes pesafiiSkeletonPulse": {
      "0%, 100%": { opacity: 0.5 },
      "50%": { opacity: 0.85 },
    },
    [`@media ${REDUCED_MOTION_QUERY}`]: {
      animation: "none",
      opacity: 0.65,
    },
  };
}

/**
 * A blanket "hold still" fragment for a container whose children animate in
 * ways this module does not own (a third-party chart's own entrance, say).
 * Spread it onto the container's `sx`.
 */
export const REDUCED_MOTION_RESET = {
  [`@media ${REDUCED_MOTION_QUERY}`]: {
    "&, & *": {
      animationDuration: "0.01ms !important",
      animationIterationCount: "1 !important",
      transitionDuration: "0.01ms !important",
      scrollBehavior: "auto !important",
    },
  },
};
